// studioDesk — what is in focus on the Studio desk (pen "Studio – the desk:
// open, lift, close"; issue #161). A module is a binder, a course a shelf of
// binders. Focus is ONE view with a path, Course › Module › Document: only
// the deepest level is shown, lifted over the frozen desk, and the levels
// above it are its breadcrumb. Pure: the path, the one reducer that changes
// it, and its parse/serialize for keeping it per device (view state, like the
// zoom; never project state).
//
// Navigation rules:
//  - in: open (double-click, Enter, Open) from the level shown: the path
//    grows by one; opening a level already on the path goes back to it;
//  - out: close (Esc, Close) goes ONE level up; the breadcrumb goes back to
//    the level it names; a click outside, on the frozen desk, ends focus;
//  - sideways: any row of the structure beside a document (a sibling, or a
//    document or module elsewhere in the course) opens from the path's
//    start, so the path is rebuilt and focus never ends;
//  - nothing closes or tidies itself.

export type { DeskCommand, DeskDocument, DeskEntry, DeskState };
export {
    DOCUMENT_FRAME,
    deskBinder,
    deskDocument,
    deskReduce,
    emptyDesk,
    frontDocument,
    inFocus,
    openCommands,
    openPartCommands,
    parseDesk,
    serializeDesk,
};

import { exhaustSwitchCase } from "@bilda/utils";
import type { DocumentFacts } from "../../app/documentFacts.js";
import type { MaterialTemplate } from "../../app/ports.js";
import type { ContainerCard, StudioCard } from "./studioCards.js";
import type { Size } from "./studioLayout.js";

// A binder (a module), a shelf (a course), or a document (anything else,
// opened to read or edit it).
type DeskKind = "binder" | "shelf" | "document";

// One level of the path.
type DeskEntry = { readonly id: string; readonly kind: DeskKind };

// From the outermost level down; the last one is shown. Empty: nothing in
// focus (the Studio). Only the last level can be a document.
type DeskState = { readonly path: readonly DeskEntry[] };

type DeskCommand =
    // One level in, from the level `from` (null: from the desk).
    | {
          readonly type: "open";
          readonly id: string;
          readonly kind: DeskKind;
          readonly from: string | null;
      }
    // One level up: `id` and the levels below it leave the path.
    | { readonly type: "close"; readonly id: string }
    // The breadcrumb to `id`: it becomes the level shown.
    | { readonly type: "close_to"; readonly id: string }
    // A click outside, on the frozen desk: focus ends.
    | { readonly type: "desk_clicked" }
    // The Undo of ending focus: exactly that path again.
    | { readonly type: "restore"; readonly state: DeskState };

const emptyDesk: DeskState = { path: [] };

// The path up to and including `id`.
const upTo = (path: readonly DeskEntry[], id: string): readonly DeskEntry[] =>
    path.slice(0, path.findIndex((e) => e.id === id) + 1);

const deskReduce = (state: DeskState, command: DeskCommand): DeskState => {
    const { path } = state;
    const has = (id: string | null) => path.some((e) => e.id === id);
    switch (command.type) {
        case "open": {
            if (has(command.id)) return { path: upTo(path, command.id) };
            const base =
                command.from !== null && has(command.from)
                    ? upTo(path, command.from)
                    : [];
            // Nothing opens from a document: it starts a new path.
            const from = base.at(-1)?.kind === "document" ? [] : base;
            return { path: [...from, { id: command.id, kind: command.kind }] };
        }
        case "close":
            return has(command.id)
                ? { path: upTo(path, command.id).slice(0, -1) }
                : state;
        case "close_to":
            return has(command.id) ? { path: upTo(path, command.id) } : state;
        case "desk_clicked":
            return emptyDesk;
        case "restore":
            return command.state;
        default:
            return exhaustSwitchCase(command);
    }
};

// The level shown, if anything is in focus.
const inFocus = (state: DeskState): DeskEntry | undefined => state.path.at(-1);

const CONFORMING: ContainerCard["conformance"] = { type: "conforming" };

// The container a level shows: a container card on the canvas, or a module
// inside a course card (it is drawn inside its course, not on its own).
// Undefined: it is no longer in the workspace.
const deskBinder = (
    cards: readonly StudioCard[],
    id: string,
): ContainerCard | undefined => {
    const containers = cards.filter(
        (c): c is ContainerCard => c.type === "container",
    );
    const own = containers.find((c) => c.id === id);
    if (own !== undefined) return own;
    const part = containers
        .flatMap((c) => c.parts)
        .find((p) => p.id === id && p.kind === "module");
    return part === undefined
        ? undefined
        : {
              type: "container",
              id: part.id,
              kind: "module",
              title: part.title,
              aiAuthored: part.aiAuthored,
              parts: part.parts,
              conformance: CONFORMING,
          };
};

// What an open document shows: found on the canvas or inside a container.
type DeskDocument = {
    readonly id: string;
    readonly title: string;
    readonly kind: "material" | "source" | "other";
    readonly template?: MaterialTemplate;
    readonly aiAuthored: boolean;
    readonly planned: boolean;
    // Where a material stands as a document, and its brief.
    readonly document?: DocumentFacts;
};

const deskDocument = (
    cards: readonly StudioCard[],
    id: string,
): DeskDocument | undefined => {
    const card = cards.find((c) => c.id === id);
    switch (card?.type) {
        case "material":
            return {
                id,
                title: card.title,
                kind: "material",
                template: card.template,
                aiAuthored: card.aiAuthored,
                planned: card.planned,
                document: card.document,
            };
        case "source":
            return {
                id,
                title: card.title,
                kind: "source",
                aiAuthored: false,
                planned: false,
            };
        case "generic":
            return {
                id,
                title: card.title,
                kind: "other",
                aiAuthored: false,
                planned: false,
            };
        case "container":
        case undefined:
            break;
        default:
            return exhaustSwitchCase(card);
    }
    const part = cards
        .flatMap((c) => (c.type === "container" ? c.parts : []))
        .flatMap((p) => [p, ...p.parts])
        .find((p) => p.id === id && p.kind !== "module");
    return part === undefined
        ? undefined
        : {
              id,
              title: part.title,
              kind: part.kind === "module" ? "other" : part.kind,
              template: part.template,
              aiAuthored: part.aiAuthored,
              planned: part.planned,
              document: part.document,
          };
};

// The document shown (the one being edited), if a document is in focus.
const frontDocument = (state: DeskState): string | undefined => {
    const shown = inFocus(state);
    return shown?.kind === "document" ? shown.id : undefined;
};

// The lifted size of an open document (pen: 1248 x 780).
const DOCUMENT_FRAME: Size = { width: 1248, height: 780 };

// Opening one part of the container shown: a module goes one level in as a
// binder, anything else as a document. A document inside a course's module
// goes in through that binder.
const openPartCommands = (
    container: ContainerCard,
    part: string,
): readonly DeskCommand[] => {
    const from = container.id;
    const own = container.parts.find((p) => p.id === part);
    if (own !== undefined)
        return [
            {
                type: "open",
                id: part,
                kind: own.kind === "module" ? "binder" : "document",
                from,
            },
        ];
    const module = container.parts.find(
        (p) => p.kind === "module" && p.parts.some((q) => q.id === part),
    );
    return module === undefined
        ? []
        : [
              { type: "open", id: module.id, kind: "binder", from },
              { type: "open", id: part, kind: "document", from: module.id },
          ];
};

// Opening a card on the desk, or one part of a container card: a module or
// course comes into focus, then the part (if any) from it; anything else is
// a document.
const openCommands = (
    card: StudioCard,
    part: string | undefined,
): readonly DeskCommand[] =>
    card.type === "container"
        ? [
              {
                  type: "open",
                  id: card.id,
                  kind: card.kind === "course" ? "shelf" : "binder",
                  from: null,
              },
              ...(part === undefined ? [] : openPartCommands(card, part)),
          ]
        : [{ type: "open", id: card.id, kind: "document", from: null }];

// --- Kept per device -----------------------------------------------------------
// JSON `[{ "id", "kind" }, …]`, from the outermost level down. Parse, don't
// validate: an entry that is malformed or of an unknown kind is left out
// (never a crash), and a path with a document anywhere but last is none.

const entryOf = (raw: unknown): readonly DeskEntry[] => {
    if (typeof raw !== "object" || raw === null) return [];
    const id = "id" in raw ? raw.id : undefined;
    const kind = "kind" in raw ? raw.kind : undefined;
    if (typeof id !== "string") return [];
    if (kind !== "binder" && kind !== "shelf" && kind !== "document") return [];
    return [{ id, kind }];
};

const parseDesk = (raw: string | null): DeskState => {
    if (raw === null) return emptyDesk;
    try {
        const value: unknown = JSON.parse(raw);
        if (!Array.isArray(value)) return emptyDesk;
        const path = value.flatMap(entryOf);
        return path.slice(0, -1).some((e) => e.kind === "document")
            ? emptyDesk
            : { path };
    } catch {
        return emptyDesk;
    }
};

const serializeDesk = (state: DeskState): string =>
    JSON.stringify(state.path.map((e) => ({ id: e.id, kind: e.kind })));
